import { createSignal, createMemo, onMount, For, Show } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import BotManageNav from '../components/BotManageNav';
import { hasRunningInstance } from '../components/BotStatusBadge';
import { BOT_ACTIONS, botAccessLabelKey, can } from '../services/resourceActions';
import type { BotRegistryDetailResponse } from '../api/generated/schemas';
import styles from './BotManage.module.css';
import { firstError } from '../api/envelope';

interface BotListEntry {
    id: string;
    minecraftUsername: string;
}

const isDeletable = (state?: string): boolean => !hasRunningInstance(state);

export default function BotManage() {
    const params = useParams();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const botId = () => params.id || '';

    const [bot, setBot] = createSignal<BotRegistryDetailResponse | null>(null);
    const [otherBots, setOtherBots] = createSignal<BotListEntry[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [copySourceId, setCopySourceId] = createSignal('');
    const [copyLoading, setCopyLoading] = createSignal(false);
    const [deleting, setDeleting] = createSignal(false);

    const canManage = () => can(bot()?.myActions, BOT_ACTIONS.ConfigWrite);
    const canDelete = () => can(bot()?.myActions, BOT_ACTIONS.Delete);

    onMount(async () => {
        const [botRes, listRes] = await Promise.all([
            api.botRegistry.getBot(botId()),
            api.botRegistry.listBots(),
        ]);
        if (isOk(botRes.data)) setBot(botRes.data.value);
        if (isOk(listRes.data)) {
            const all = (listRes.data.value.bots ?? []) as unknown as BotListEntry[];
            setOtherBots(all.filter(b => b.id !== botId()));
        }
        setLoading(false);
    });

    const copySettings = async () => {
        if (!copySourceId()) return;
        setCopyLoading(true);
        const res = await api.botRegistry.copySettings(botId(), { fromBotId: copySourceId() });
        setCopyLoading(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_REGISTRY_SETTINGS_COPIED'));
            setCopySourceId('');
        } else {
            toast?.error(t('BOT_MANAGE_COPY_FAILED'));
        }
    };

    const deleteBot = async () => {
        const username = bot()?.minecraftUsername ?? '';
        if (!await showConfirm(t('BOT_REGISTRY_DELETE_CONFIRM_1', username), true)) return;
        if (!await showConfirm(t('BOT_REGISTRY_DELETE_CONFIRM_2', username), true)) return;
        setDeleting(true);
        const res = await api.botRegistry.deleteBotRegistry(botId());
        setDeleting(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_REGISTRY_DELETED', username));
            navigate('/bots');
        } else {
            toast?.error(firstError(res.data) ?? t('BOT_REGISTRY_DELETE_FAILED'));
        }
    };

    const levelLabel = createMemo(() => {
        const b = bot();
        if (!b) return '';
        return t(botAccessLabelKey(b.myActions));
    });

    return (
        <div class={styles.container}>
            <BotManageNav botId={botId()} />

            <Show when={!loading()} fallback={
                <div class={styles.loadingCenter}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            }>
                <div class="fcard v">
                    <h2 class={styles.sectionHeader}>
                        <i class="fa-solid fa-circle-info" /> <Text val="BOT_MANAGE_OVERVIEW" />
                    </h2>
                    <div class={styles.metaCard}>
                        <div class={styles.metaRow}>
                            <span class={styles.metaKey}><Text val="BOT_MANAGE_USERNAME" /></span>
                            <span class={styles.metaVal}>{bot()?.minecraftUsername}</span>
                        </div>
                        <div class={styles.metaRow}>
                            <span class={styles.metaKey}><Text val="BOT_MANAGE_UUID" /></span>
                            <span class={styles.metaVal}>{bot()?.minecraftUuid}</span>
                        </div>
                        <div class={styles.metaRow}>
                            <span class={styles.metaKey}><Text val="BOT_MANAGE_ACCESS_LEVEL" /></span>
                            <span class={styles.metaVal}>{levelLabel()}</span>
                        </div>
                        <div class={styles.metaRow}>
                            <span class={styles.metaKey}><Text val="BOT_MANAGE_STATE" /></span>
                            <span class={styles.metaVal}>{bot()?.state}</span>
                        </div>
                    </div>
                </div>

                <Show when={canManage()}>
                    <div class="fcard v" style={{ 'margin-top': 'var(--spacing-lg)' }}>
                        <h2 class={styles.sectionHeader}>
                            <i class="fa-solid fa-copy" /> <Text val="BOT_REGISTRY_COPY_SETTINGS" />
                        </h2>
                        <Show when={otherBots().length > 0} fallback={
                            <p style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)', margin: 0 }}>
                                <Text val="BOT_MANAGE_NO_OTHER_BOTS" />
                            </p>
                        }>
                            <div class={styles.inlineForm}>
                                <select
                                    class={styles.select}
                                    value={copySourceId()}
                                    onInput={(e) => setCopySourceId(e.currentTarget.value)}
                                >
                                    <option value="">{t('BOT_REGISTRY_SELECT_SOURCE')}</option>
                                    <For each={otherBots()}>{(src) => (
                                        <option value={src.id}>{src.minecraftUsername}</option>
                                    )}</For>
                                </select>
                                <button
                                    class="btn btn-primary"
                                    disabled={!copySourceId() || copyLoading()}
                                    onClick={copySettings}
                                >
                                    {copyLoading() ? <Text val="BOT_REGISTRY_COPYING" /> : <Text val="BOT_REGISTRY_COPY" />}
                                </button>
                            </div>
                        </Show>
                    </div>
                </Show>

                <Show when={canDelete()}>
                    <div class={styles.dangerZone}>
                        <div class={styles.dangerTitle}><Text val="BOT_MANAGE_DANGER_ZONE" /></div>
                        <div class={styles.dangerRow}>
                            <span class={styles.dangerDesc}><Text val="BOT_MANAGE_DELETE_DESC" /></span>
                            <button
                                class="btn btn-danger"
                                disabled={!isDeletable(bot()?.state) || deleting()}
                                title={!isDeletable(bot()?.state) ? t('BOT_REGISTRY_STOP_BEFORE_DELETE') : undefined}
                                onClick={deleteBot}
                            >
                                <i class={deleting() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-trash'} />
                                {' '}{deleting() ? <Text val="BOT_REGISTRY_DELETING" /> : <Text val="DELETE" />}
                            </button>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
